import styles from "./testCaseIncomingMenu.module.scss";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import {useEffect, useState} from "react";
import { ReactComponent as AddIcon } from "../../shared/images/add.svg";
import { useTranslation } from "react-i18next";
import { TestCaseIncoming } from "../../types/testCaseIncomingTypes";
import { TestCaseIncomingModal } from "../testCaseIncomingModal/testCaseIncomingModal";
import {
  useDeleteTestCaseIncomingMutation,
  useGetAllTestCaseIncomingQuery
} from "../../api/testCaseIncomingService";
import { TestCaseIncomingItem } from "./testCaseIncomingItem/testCaseIncomingItem";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import {
  setShowSpinner,
  setUpdateModal
} from "../../store/slices/mainSlice";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import {
  deleteSelectedTestCaseIncoming,
  setSelectedTestCaseIncoming
} from "../../store/slices/testCaseIncomingSlice";

export const TestCaseIncomingMenu = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const selectedTestCaseIncoming = useSelector((state: RootState) => state.testCaseIncomingSlice.selectedTestCaseIncoming);

  const [showTestCaseIncomingModal, setShowTestCaseIncomingModal] = useState<boolean>(false)
  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false)

  const [searchValue, setSearchValue] = useState<string>("")
  const [editTestCaseIncoming, setEditTestCaseIncoming] = useState<TestCaseIncoming>()
  const [filteredTestCaseIncomings, setFilteredTestCaseIncomings] = useState<Array<TestCaseIncoming>>([]);

  const { data: testCaseIncomings, refetch: refetchTestCaseIncomings, isLoading: isLoadingTestCases, isFetching: isFetchingTestCases } = useGetAllTestCaseIncomingQuery("", { refetchOnMountOrArgChange: true });
  const [deleteTestCaseIncomingMutation, { isSuccess: isSuccessDeleteTestCaseIncoming }] = useDeleteTestCaseIncomingMutation();

  useEffect(() => {
    dispatch(setShowSpinner(isLoadingTestCases || isFetchingTestCases));
  }, [isLoadingTestCases, isFetchingTestCases]);

  useEffect(() => {
    if (!!testCaseIncomings) {
      setFilteredTestCaseIncomings(testCaseIncomings);
    }
  }, [testCaseIncomings]);

  useEffect(() => {
    if (!!testCaseIncomings) {
      setFilteredTestCaseIncomings(testCaseIncomings.filter(item => item.name.toLowerCase().includes(searchValue.toLowerCase())));
    }
  }, [searchValue, testCaseIncomings]);

  useEffect(() => {
    if (!!filteredTestCaseIncomings.length && !!selectedTestCaseIncoming.id) {
      const findIndex = filteredTestCaseIncomings.findIndex(item => item.id === selectedTestCaseIncoming.id);
      if (findIndex > -1) {
        dispatch(setSelectedTestCaseIncoming(filteredTestCaseIncomings[findIndex]));
      } else {
        dispatch(deleteSelectedTestCaseIncoming());
      }
    }
  }, [filteredTestCaseIncomings]);

  useEffect(() => {
    if (isSuccessDeleteTestCaseIncoming) {
      refetchTestCaseIncomings();
      dispatch(setUpdateModal({ show: true, isDelete: true }))
    }
  }, [isSuccessDeleteTestCaseIncoming]);


  const onClickAdd = (): void => {
    setShowTestCaseIncomingModal(true);
  }

  const closeTestCaseIncomingModal = (isSave: boolean, createId?: number): void => {
    setShowTestCaseIncomingModal(false);
    setEditTestCaseIncoming(undefined);
    if (isSave) {
      if (!!createId) {
        const newSelectedTestCaseIncoming = {...selectedTestCaseIncoming};
        newSelectedTestCaseIncoming.id = createId;
        dispatch(setSelectedTestCaseIncoming(newSelectedTestCaseIncoming));
      }
      refetchTestCaseIncomings();
    }
  }

  const editTestCaseClick = (testCase: TestCaseIncoming): void => {
    setEditTestCaseIncoming(testCase);
    setShowTestCaseIncomingModal(true);
  }

  const deleteTestCaseClick = (testCase: TestCaseIncoming): void => {
    setEditTestCaseIncoming(testCase);
    setShowDeleteMessage(true);
  }

  const copyTestCaseClick = (testCaseTemplate: TestCaseIncoming): void => {
    const newTestCase: TestCaseIncoming = {...testCaseTemplate};
    newTestCase.id = undefined;
    newTestCase.name = `${testCaseTemplate.name} - copy`;
    setEditTestCaseIncoming(newTestCase);
    setShowTestCaseIncomingModal(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!editTestCaseIncoming?.id) {
      deleteTestCaseIncomingMutation(editTestCaseIncoming.id);
    }
    setShowDeleteMessage(false);
    setEditTestCaseIncoming(undefined);
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.search}>
            <SearchInput onChange={setSearchValue} value={searchValue}/>
          </div>
          <div className={styles.addTestCase} onClick={onClickAdd}>
            <AddIcon/>
            {t("testCaseIncoming.add")}
          </div>
        </div>
        <div className={styles.testCases}>
          {filteredTestCaseIncomings.map((testCaseIncoming, index) => (
            <TestCaseIncomingItem testCaseIncoming={testCaseIncoming}
                                  key={index}
                                  onDeleteClick={() => deleteTestCaseClick(testCaseIncoming)}
                                  onCopyClick={() => copyTestCaseClick(testCaseIncoming)}
                                  onEditClick={() => editTestCaseClick(testCaseIncoming)}/>
          ))}
        </div>
      </div>
      {showTestCaseIncomingModal && (<TestCaseIncomingModal onClose={closeTestCaseIncomingModal}
                                                            testCaseIncoming={editTestCaseIncoming}/>)}
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("testCaseIncoming.delete")}
                       description={t("testCaseIncoming.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>
  );
}